import { useEffect, useState } from "react";
import {
  fetchAdminMediaKitStats,
  refreshAutomaticMediaKitStats,
  updateTikTokMediaKitStats,
} from "../../api/mediaKit";
import { PLATFORM_LABELS, formatCompactNumber, formatUpdatedDate } from "../../mediaKit/model";

const pairsToText = (items = []) => items.map((item) => `${item.title} | ${item.description}`).join("\n");
const textToPairs = (value) => value.split("\n").map((line) => {
  const [title, ...description] = line.split("|");
  return { title: title.trim(), description: description.join("|").trim() };
}).filter((item) => item.title && item.description);

const countriesToText = (countries = []) => countries
  .map((country) => `${country.code} | ${country.name} | ${country.percent}`)
  .join("\n");

const textToCountries = (value) => value.split("\n").map((line) => {
  const [code, name, percent] = line.split("|").map((part) => part.trim());
  return { code: code?.toUpperCase(), name, percent: Number(percent) };
}).filter((country) => country.code && country.name && Number.isFinite(country.percent));

const blankTikTok = {
  followersCount: "",
  views28d: "",
  discoveryPercent: "",
  femalePercent: "",
  age1844Percent: "",
  countries: [],
  periodStart: "",
  periodEnd: "",
};

export default function MediaKitEditor({ content, saving, onChange, onSubmit }) {
  const [stats, setStats] = useState([]);
  const [tiktok, setTikTok] = useState(blankTikTok);
  const [countryDraft, setCountryDraft] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [brandFitDraft, setBrandFitDraft] = useState(() => pairsToText(content.brandFitPoints));
  const [opportunitiesDraft, setOpportunitiesDraft] = useState(() => pairsToText(content.opportunities));
  const [categoriesDraft, setCategoriesDraft] = useState(() => content.categories.join(", "));

  const loadStats = async () => {
    const rows = await fetchAdminMediaKitStats();
    setStats(rows);
    const savedTikTok = rows.find((row) => row.platform === "tiktok");
    if (savedTikTok) {
      setTikTok({ ...savedTikTok });
      setCountryDraft(countriesToText(savedTikTok.countries));
    }
  };

  useEffect(() => {
    loadStats().catch((error) => setMessage({ type: "error", text: error.message }));
  }, []);

  const updateTikTok = (field, value) => setTikTok((current) => ({ ...current, [field]: value }));

  async function saveTikTok(event) {
    event.preventDefault();
    setBusy("tiktok");
    setMessage({ type: "", text: "" });
    try {
      const saved = await updateTikTokMediaKitStats({ ...tiktok, countries: textToCountries(countryDraft) });
      setTikTok({ ...saved });
      setCountryDraft(countriesToText(saved.countries));
      setStats((current) => current.map((stat) => stat.platform === "tiktok" ? saved : stat));
      setMessage({ type: "success", text: "TikTok media-kit data published." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy("");
    }
  }

  async function refreshAutomatic() {
    setBusy("refresh");
    setMessage({ type: "", text: "" });
    try {
      const result = await refreshAutomaticMediaKitStats();
      await loadStats();
      const preserved = result.results?.filter((item) => item.status !== "updated") || [];
      setMessage({
        type: preserved.length ? "error" : "success",
        text: preserved.length
          ? `${result.updated} sources refreshed; previous data was preserved for ${preserved.map((item) => PLATFORM_LABELS[item.platform]).join(", ")}.`
          : "YouTube, Instagram, and Facebook data refreshed.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="admin-media-kit-stack">
      {message.text && <p className={`admin-message ${message.type}`} role="status">{message.text}</p>}

      <section className="admin-editor admin-social-status">
        <div className="admin-editor-heading">
          <div><p className="admin-kicker">Audience data</p><h2>Platform statistics</h2><p>Automatic sources retain their last successful snapshot. TikTok is maintained manually.</p></div>
          <button type="button" onClick={refreshAutomatic} disabled={Boolean(busy)}>{busy === "refresh" ? "Refreshing…" : "Refresh Google & Meta"}</button>
        </div>
        <div className="admin-platform-status-grid">
          {stats.map((stat) => <article key={stat.platform}><strong>{PLATFORM_LABELS[stat.platform]}</strong><span>{formatCompactNumber(stat.followersCount)} followers</span><span>{formatCompactNumber(stat.views28d)} views</span><small>{formatUpdatedDate(stat.capturedAt)} · {stat.source.replaceAll("_", " ")}</small></article>)}
        </div>
      </section>

      <form className="admin-editor" onSubmit={saveTikTok}>
        <div className="admin-editor-heading"><div><p className="admin-kicker">Manual source</p><h2>TikTok data</h2><p>Enter the values shown in TikTok Analytics for the same completed 28-day period used by the other platforms.</p></div></div>
        <div className="admin-stat-grid admin-stat-grid-wide">
          <label>Followers<input type="number" min="0" value={tiktok.followersCount ?? ""} onChange={(event) => updateTikTok("followersCount", event.target.value)} required /></label>
          <label>Views in 28 days<input type="number" min="0" value={tiktok.views28d ?? ""} onChange={(event) => updateTikTok("views28d", event.target.value)} required /></label>
          <label>Non-follower views %<input type="number" min="0" max="100" step="0.1" value={tiktok.discoveryPercent ?? ""} onChange={(event) => updateTikTok("discoveryPercent", event.target.value)} /></label>
          <label>Female audience %<input type="number" min="0" max="100" step="0.1" value={tiktok.femalePercent ?? ""} onChange={(event) => updateTikTok("femalePercent", event.target.value)} /></label>
          <label>Audience aged 18–44 %<input type="number" min="0" max="100" step="0.1" value={tiktok.age1844Percent ?? ""} onChange={(event) => updateTikTok("age1844Percent", event.target.value)} /></label>
          <label>Period start<input type="date" value={tiktok.periodStart || ""} onChange={(event) => updateTikTok("periodStart", event.target.value)} required /></label>
          <label>Period end<input type="date" value={tiktok.periodEnd || ""} onChange={(event) => updateTikTok("periodEnd", event.target.value)} required /></label>
        </div>
        <label>Top countries <small>One per line: country code | country name | percentage</small><textarea rows="6" value={countryDraft} onChange={(event) => setCountryDraft(event.target.value)} placeholder="US | United States | 25.4" /></label>
        <button className="admin-primary" disabled={Boolean(busy)}>{busy === "tiktok" ? "Publishing…" : "Publish TikTok data"}</button>
      </form>

      <form className="admin-editor" onSubmit={onSubmit}>
        <div className="admin-editor-heading">
          <div><p className="admin-kicker">Page content</p><h2>Media Kit copy</h2><p>Changes publish immediately to the page and its downloadable PDF.</p></div>
          <a href="/media-kit" target="_blank" rel="noopener noreferrer">View page</a>
        </div>
        <label>Kicker<input value={content.kicker} onChange={(event) => onChange("kicker", event.target.value)} required /></label>
        <label>Page title<input value={content.title} onChange={(event) => onChange("title", event.target.value)} required /></label>
        <label>Introduction<textarea rows="4" value={content.intro} onChange={(event) => onChange("intro", event.target.value)} required /></label>
        <label>Hero image URL<input type="url" value={content.heroImageUrl} onChange={(event) => onChange("heroImageUrl", event.target.value)} required /></label>
        <label>Hero image description<input value={content.heroImageAlt} onChange={(event) => onChange("heroImageAlt", event.target.value)} required /></label>
        <label>Brand-fit heading<input value={content.brandFitTitle} onChange={(event) => onChange("brandFitTitle", event.target.value)} required /></label>
        <label>Brand-fit introduction<textarea rows="3" value={content.brandFitIntro} onChange={(event) => onChange("brandFitIntro", event.target.value)} required /></label>
        <label>Brand-fit points <small>One per line: title | description</small><textarea rows="7" value={brandFitDraft} onChange={(event) => { setBrandFitDraft(event.target.value); onChange("brandFitPoints", textToPairs(event.target.value)); }} required /></label>
        <label>Discovery heading<input value={content.discoveryTitle} onChange={(event) => onChange("discoveryTitle", event.target.value)} required /></label>
        <label>Discovery introduction<textarea rows="3" value={content.discoveryIntro} onChange={(event) => onChange("discoveryIntro", event.target.value)} required /></label>
        <label>Audience heading<input value={content.audienceTitle} onChange={(event) => onChange("audienceTitle", event.target.value)} required /></label>
        <label>Audience introduction<textarea rows="3" value={content.audienceIntro} onChange={(event) => onChange("audienceIntro", event.target.value)} required /></label>
        <label>Audience supporting copy<textarea rows="3" value={content.audienceDetail} onChange={(event) => onChange("audienceDetail", event.target.value)} required /></label>
        <label>Best-fit categories <small>Separate categories with commas.</small><input value={categoriesDraft} onChange={(event) => { setCategoriesDraft(event.target.value); onChange("categories", event.target.value.split(",").map((item) => item.trim()).filter(Boolean)); }} required /></label>
        <fieldset className="admin-platform-copy"><legend>Platform descriptions</legend>{Object.entries(content.platformDescriptions).map(([platform, description]) => <label key={platform}>{PLATFORM_LABELS[platform]}<input value={description} onChange={(event) => onChange("platformDescriptions", { ...content.platformDescriptions, [platform]: event.target.value })} required /></label>)}</fieldset>
        <label>Opportunities heading<input value={content.opportunitiesTitle} onChange={(event) => onChange("opportunitiesTitle", event.target.value)} required /></label>
        <label>Opportunities introduction<textarea rows="3" value={content.opportunitiesIntro} onChange={(event) => onChange("opportunitiesIntro", event.target.value)} required /></label>
        <label>Partnership opportunities <small>One per line: title | description</small><textarea rows="9" value={opportunitiesDraft} onChange={(event) => { setOpportunitiesDraft(event.target.value); onChange("opportunities", textToPairs(event.target.value)); }} required /></label>
        <label>Call-to-action heading<input value={content.ctaTitle} onChange={(event) => onChange("ctaTitle", event.target.value)} required /></label>
        <label>Call-to-action copy<textarea rows="3" value={content.ctaText} onChange={(event) => onChange("ctaText", event.target.value)} required /></label>
        <label>Contact email<input type="email" value={content.contactEmail} onChange={(event) => onChange("contactEmail", event.target.value)} required /></label>
        <label>Website URL<input type="url" value={content.websiteUrl} onChange={(event) => onChange("websiteUrl", event.target.value)} required /></label>
        <button className="admin-primary" disabled={saving}>{saving ? "Publishing…" : "Publish Media Kit"}</button>
      </form>
    </div>
  );
}
